import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import dayjs from 'dayjs';
import TitleSectionBack from 'Containers/Common/TitleSectionBack';
import Modal from 'Containers/Common/Modal';
import ConfirmDeleteModal from 'Containers/Common/ConfirmDeleteModal';
import ConfirmStatusModal from 'Containers/Common/ConfirmStatusModal';
import useWindowSize from 'utils/useWindowSize';
import { ROUTES } from 'routes/constants';
import { PREMIUM_UPGRADE_PATH } from 'utils/premiumUpgradePath';
import { getActiveSalon } from 'Containers/Auth/store/selectors';
import { salonManagementActions } from 'Containers/SalonManagement/store';
import { getWorkers } from 'Containers/SalonManagement/store/selectors';
import useIsPremiumUnlocked from 'utils/useIsPremiumUnlocked';
import { modalActions } from 'Containers/Modals/store';
import {
  Armchair,
  ChevronLeft,
  ChevronRight,
  Home,
  LayoutList,
  MoreHorizontal,
  Package,
  Pencil,
  Plus,
  Receipt,
  Repeat,
  Trash2,
  Wallet,
} from 'lucide-react';
import ExpenseModal from './ExpenseModal';
import { expensesActions } from './store';
import {
  getExpenses,
  getExpensesLoading,
} from './store/selectors';
import {
  FILTER_IDS,
  filterByMonth,
  filterByPill,
  findChair,
  formatEuro,
  formatMonthLabel,
  formatRecurringBadge,
  getChairs,
  getExpenseAmount,
  sumDue,
  sumExpenses,
  sumPaid,
} from './utils';
import styles from './Expenses.module.scss';

const FILTERS = [
  { id: FILTER_IDS.ALL, key: 'all', icon: LayoutList },
  { id: FILTER_IDS.PERIODICHE, key: 'recurring', icon: Repeat },
  { id: FILTER_IDS.STAFF, key: 'staff', icon: Armchair },
  { id: FILTER_IDS.BOLLETTE, key: 'bills', icon: Receipt },
  { id: FILTER_IDS.AFFITTO, key: 'rent', icon: Home },
  { id: FILTER_IDS.MATERIALI, key: 'materials', icon: Package },
  { id: FILTER_IDS.ALTRO, key: 'other', icon: MoreHorizontal },
];

const ExpensesList = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  const activeSalon = useSelector(getActiveSalon());
  const apiWorkers = useSelector(getWorkers());
  const expenses = useSelector(getExpenses());
  const loadingExpenses = useSelector(getExpensesLoading());
  const chairs = useMemo(() => getChairs(apiWorkers), [apiWorkers]);

  const isPremiumUnlocked = useIsPremiumUnlocked();

  const [period, setPeriod] = useState(() => dayjs().startOf('month'));
  const [activeFilterId, setActiveFilterId] = useState(FILTER_IDS.ALL);
  const [modalExpense, setModalExpense] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [expenseToToggle, setExpenseToToggle] = useState(null);

  const fetchExpenses = useCallback(
    (monthPeriod = period, { force = false } = {}) => {
      dispatch(
        expensesActions.GET_EXPENSES.REQUEST(
          {
            month: monthPeriod.format('YYYY-MM'),
            force,
          },
          null,
          { skipLoader: true },
        ),
      );
    },
    [dispatch, period],
  );

  useEffect(() => {
    if (isPremiumUnlocked) return;
    navigate(PREMIUM_UPGRADE_PATH, { replace: true });
  }, [isPremiumUnlocked, navigate]);

  useEffect(() => {
    if (!isPremiumUnlocked) return;
    dispatch(salonManagementActions.GET_WORKERS.REQUEST({}, null, { skipLoader: true }));
  }, [dispatch, isPremiumUnlocked]);

  useEffect(() => {
    if (!isPremiumUnlocked) return;
    fetchExpenses(period);
  }, [period, fetchExpenses, isPremiumUnlocked]);

  const monthExpenses = useMemo(() => filterByMonth(expenses, period), [expenses, period]);
  const filteredExpenses = useMemo(
    () =>
      filterByPill(monthExpenses, activeFilterId).sort((a, b) => b.data.localeCompare(a.data)),
    [monthExpenses, activeFilterId],
  );

  const periodDue = sumDue(monthExpenses, chairs);
  const periodPaid = sumPaid(monthExpenses, chairs);
  const periodTotal = sumExpenses(monthExpenses, chairs);
  const filteredDue = sumDue(filteredExpenses, chairs);
  const filteredPaid = sumPaid(filteredExpenses, chairs);
  const filteredTotal = sumExpenses(filteredExpenses, chairs);
  const isAllFilter = activeFilterId === FILTER_IDS.ALL;

  const language = i18n.language;
  const isCurrentMonth = period.isSame(dayjs(), 'month');
  /** Finestra ampia per il select mese (si recentra sul periodo visualizzato). */
  const monthOptions = useMemo(() => {
    const start = period.subtract(120, 'month').startOf('month');
    const end = period.add(36, 'month').startOf('month');
    const options = [];
    let cursor = start;
    while (!cursor.isAfter(end, 'month')) {
      options.push({
        value: cursor.format('YYYY-MM'),
        label: formatMonthLabel(cursor, language),
      });
      cursor = cursor.add(1, 'month');
    }
    return options;
  }, [period, language]);

  const refreshData = () => {
    fetchExpenses(period, { force: true });
  };

  const openCreateModal = () => {
    setModalExpense(null);
    setIsModalOpen(true);
  };

  const openEditModal = (expense) => {
    setModalExpense(expense);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalExpense(null);
  };

  const handleSave = (payload) => {
    if (modalExpense?.id) {
      dispatch(
        expensesActions.UPDATE_EXPENSE.REQUEST(
          {
            expense: modalExpense,
            changes: payload,
            chairs,
          },
          () => {
            closeModal();
            refreshData();
          },
        ),
      );
      return;
    }

    dispatch(
      expensesActions.CREATE_EXPENSE.REQUEST(
        {
          expense: payload,
          chairs,
        },
        () => {
          closeModal();
          refreshData();
        },
      ),
    );
  };

  const confirmDelete = (deleteFuture = false) => {
    const expense = expenseToDelete;
    if (!expense) return;
    const idExpense = expense.idExpense || expense.id;

    if (deleteFuture && expense.recurrenceId) {
      dispatch(
        expensesActions.DELETE_RECURRING_EXPENSE.REQUEST(
          { idExpense },
          () => {
            setExpenseToDelete(null);
            closeModal();
            refreshData();
          },
        ),
      );
      return;
    }

    dispatch(
      expensesActions.DELETE_EXPENSE.REQUEST(
        { idExpense },
        () => {
          setExpenseToDelete(null);
          closeModal();
          refreshData();
        },
      ),
    );
  };

  const confirmToggleStatus = () => {
    const current = expenses.find((e) => e.id === expenseToToggle);
    if (!current) {
      setExpenseToToggle(null);
      return;
    }

    const nextStatus = current.stato === 'pagato' ? 'da_pagare' : 'pagato';
    dispatch(
      expensesActions.UPDATE_EXPENSE.REQUEST(
        {
          expense: current,
          changes: { stato: nextStatus },
          chairs,
        },
        () => {
          setExpenseToToggle(null);
          refreshData();
        },
      ),
    );
  };

  const getRowCopy = (expense) => {
    if (expense.tipo === 'staff') {
      const chair = findChair(expense.poltronaId, chairs);
      return {
        title:
          expense.bonusPct > 0
            ? t('settings.expenses.list.salaryWithBonus', { pct: expense.bonusPct })
            : t('settings.expenses.list.salary'),
        subtitle: t('settings.expenses.list.staffLine', { name: chair.name }),
        periodica: Boolean(expense.periodica || expense.recurrenceId),
      };
    }
    return {
      title: t(`settings.expenses.categories.${expense.categoria}`),
      subtitle: expense.note
        ? t('settings.expenses.list.operativeLineWithNote', { note: expense.note })
        : t('settings.expenses.list.operativeLine'),
      periodica: Boolean(expense.periodica || expense.recurrenceId),
    };
  };

  const addExpenseButton = (
    <button type="button" className={styles.add_expense} onClick={openCreateModal}>
      <Plus size={16} strokeWidth={2.4} />
      <span>{t('settings.expenses.add')}</span>
    </button>
  );

  if (!isPremiumUnlocked) {
    return null;
  }

  return (
    <div
      className={clsx(styles.expenses_container, {
        [styles.expenses_container_mobile]: isMobile,
      })}
    >
      <div className={styles.header}>
        <TitleSectionBack
          title={t('settings.expenses.title')}
          description={t('settings.expenses.description')}
        />
        {!isMobile && addExpenseButton}
      </div>

      <div className={styles.toolbar_card}>
        {isMobile && addExpenseButton}
        <div className={styles.month_nav}>
          <button
            type="button"
            className={styles.month_button}
            aria-label={t('settings.expenses.previousMonth')}
            onClick={() =>
              setPeriod((current) => current.subtract(1, 'month').startOf('month'))
            }
          >
            <ChevronLeft size={18} strokeWidth={2.2} />
          </button>
          <label className={styles.month_picker}>
            <span className={styles.month_label}>{formatMonthLabel(period, language)}</span>
            <select
              className={styles.month_select}
              value={period.format('YYYY-MM')}
              onChange={(event) => {
                setPeriod(dayjs(event.target.value).startOf('month'));
              }}
              aria-label={formatMonthLabel(period, language)}
            >
              {monthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={styles.month_button}
            aria-label={t('settings.expenses.nextMonth')}
            onClick={() => setPeriod((current) => current.add(1, 'month').startOf('month'))}
          >
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>
          {!isCurrentMonth && (
            <button
              type="button"
              className={styles.this_month}
              onClick={() => setPeriod(dayjs().startOf('month'))}
            >
              {t('settings.expenses.thisMonth')}
            </button>
          )}
        </div>
      </div>

      {loadingExpenses ? (
        <div className={styles.recap_card} aria-busy="true" aria-label="loading">
          <div className={styles.skel_recap}>
            <div className={styles.skel_recap_item}>
              <div className={clsx(styles.skel_line, styles.skel_sm)} />
              <div className={clsx(styles.skel_line, styles.skel_lg)} />
            </div>
            <div className={styles.skel_recap_item}>
              <div className={clsx(styles.skel_line, styles.skel_sm)} />
              <div className={clsx(styles.skel_line, styles.skel_lg)} />
            </div>
            <div className={styles.skel_recap_item}>
              <div className={clsx(styles.skel_line, styles.skel_sm)} />
              <div className={clsx(styles.skel_line, styles.skel_lg)} />
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.recap_card}>
          <div className={styles.recap_grid}>
            <div className={clsx(styles.recap_item, styles.total)}>
              <div className={styles.label}>{t('settings.expenses.summary.total')}</div>
              <div className={styles.baseline}>
                {!isAllFilter ? formatEuro(periodTotal, language) : null}
              </div>
              <div className={styles.value}>{formatEuro(filteredTotal, language)}</div>
            </div>
            <div className={styles.recap_item}>
              <div className={styles.label}>{t('settings.expenses.summary.paid')}</div>
              <div className={styles.baseline}>
                {!isAllFilter ? formatEuro(periodPaid, language) : null}
              </div>
              <div className={styles.value}>{formatEuro(filteredPaid, language)}</div>
            </div>
            <div className={clsx(styles.recap_item, styles.due)}>
              <div className={styles.label}>{t('settings.expenses.summary.due')}</div>
              <div className={styles.baseline}>
                {!isAllFilter ? formatEuro(periodDue, language) : null}
              </div>
              <div className={styles.value}>{formatEuro(filteredDue, language)}</div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.list_card}>
        <div className={styles.filters_row}>
          {FILTERS.map((filter) => {
            const Icon = filter.icon;
            const isActive = activeFilterId === filter.id;
            return (
              <button
                key={filter.id}
                type="button"
                className={clsx(styles.filter_chip, { [styles.filter_chip_selected]: isActive })}
                aria-pressed={isActive}
                onClick={() => setActiveFilterId(filter.id)}
              >
                <span className={styles.filter_chip_icon}>
                  <Icon size={15} strokeWidth={2.2} />
                </span>
                <span className={styles.filter_chip_name}>
                  {t(`settings.expenses.filters.${filter.key}`)}
                </span>
              </button>
            );
          })}
        </div>

        {loadingExpenses ? (
          <div className={styles.skel_list} aria-busy="true" aria-label="loading">
            <div className={styles.skel_row} />
            <div className={styles.skel_row} />
            <div className={styles.skel_row} />
          </div>
        ) : filteredExpenses.length ? (
          <div className={styles.list}>
            {filteredExpenses.map((expense) => {
              const copy = getRowCopy(expense);
              const amount = getExpenseAmount(expense, chairs);
              return (
                <div key={expense.id} className={styles.row}>
                  <div className={clsx(styles.avatar, { [styles.staff]: expense.tipo === 'staff' })}>
                    {expense.tipo === 'staff' ? (
                      <Armchair size={18} strokeWidth={2.2} />
                    ) : (
                      <Wallet size={18} strokeWidth={2.2} />
                    )}
                  </div>
                  <div className={styles.row_main}>
                    <div className={styles.row_title}>
                      {copy.title}
                      {copy.periodica && (
                        <span className={styles.recurring_badge}>
                          <Repeat size={12} strokeWidth={2.4} />
                          {formatRecurringBadge(expense, language)}
                        </span>
                      )}
                    </div>
                    <div className={styles.row_sub}>{copy.subtitle}</div>
                  </div>
                  <div className={styles.row_meta}>
                    <div className={styles.row_date}>
                      {dayjs(expense.data).locale(language).format('DD/MM/YYYY')}
                    </div>
                    <button
                      type="button"
                      className={clsx(
                        styles.status_pill,
                        expense.stato === 'pagato' ? styles.paid : styles.due,
                      )}
                      onClick={() => setExpenseToToggle(expense.id)}
                      title={t('settings.expenses.status.toggleHint')}
                    >
                      {expense.stato === 'pagato'
                        ? t('settings.expenses.status.paid')
                        : t('settings.expenses.status.due')}
                    </button>
                    <div className={styles.row_amount}>{formatEuro(amount, language)}</div>
                  </div>
                  <div className={styles.row_actions}>
                    <button
                      type="button"
                      className={styles.icon_button}
                      aria-label={t('common.actions.edit')}
                      onClick={() => openEditModal(expense)}
                    >
                      <Pencil size={14} strokeWidth={2.2} />
                    </button>
                    <button
                      type="button"
                      className={clsx(styles.icon_button, styles.delete_icon)}
                      aria-label={t('common.actions.delete')}
                      onClick={() => setExpenseToDelete(expense)}
                    >
                      <Trash2 size={14} strokeWidth={2.2} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.empty}>
            <Wallet size={22} strokeWidth={2.2} aria-hidden="true" />
            <strong>{t('settings.expenses.empty')}</strong>
            <span>{t('settings.expenses.emptyHint')}</span>
          </div>
        )}
      </div>

      <Modal
        isModalOpen={isModalOpen}
        setIsModalOpen={(open) => {
          if (!open) closeModal();
        }}
        title={
          modalExpense
            ? t('settings.expenses.form.editTitle')
            : t('settings.expenses.form.createTitle')
        }
        showCloseBtn
        maxWidth="520px"
        width="520px"
      >
        {isModalOpen && (
          <ExpenseModal
            expense={modalExpense}
            chairs={chairs}
            period={period}
            onSave={handleSave}
            closeModal={closeModal}
          />
        )}
      </Modal>

      <ConfirmDeleteModal
        isModalOpen={!!expenseToDelete}
        setIsModalOpen={(open) => {
          if (!open) setExpenseToDelete(null);
        }}
        title={
          expenseToDelete?.recurrenceId
            ? t('settings.expenses.confirmDeleteRecurring')
            : t('settings.expenses.confirmDelete')
        }
        width={expenseToDelete?.recurrenceId ? '520px' : '450px'}
        buttons={
          expenseToDelete?.recurrenceId
            ? [
                t('common.actions.cancel'),
                t('settings.expenses.deleteOnlyThis'),
                t('settings.expenses.deleteAllFuture'),
              ]
            : [t('common.actions.cancel'), t('common.actions.delete')]
        }
        buttonVariants={
          expenseToDelete?.recurrenceId
            ? ['default', 'dangerLight', 'danger']
            : ['default', 'danger']
        }
        showChevron={false}
        buttonsActions={
          expenseToDelete?.recurrenceId
            ? [
                () => setExpenseToDelete(null),
                () => confirmDelete(false),
                () => confirmDelete(true),
              ]
            : [() => setExpenseToDelete(null), () => confirmDelete(false)]
        }
        centered
      />

      <ConfirmStatusModal
        isModalOpen={!!expenseToToggle}
        setIsModalOpen={(open) => {
          if (!open) setExpenseToToggle(null);
        }}
        targetStatus={
          expenses.find((e) => e.id === expenseToToggle)?.stato === 'pagato'
            ? 'da_pagare'
            : 'pagato'
        }
        action={confirmToggleStatus}
      />
    </div>
  );
};

export default ExpensesList;
